import { addDays, eachDayOfInterval, format, subDays } from "date-fns";
import { DashboardConfigUrl, DashboardConfigWidget, DashboardCustomLink } from "../types";

export const ACTION_REQUIRED_DASHBOARD_WIDGET_CODE = "action_required_count";
export const ACTION_REQUIRED_DASHBOARD_WIDGET_CODES = [ACTION_REQUIRED_DASHBOARD_WIDGET_CODE, "action_required"];

export const DEFAULT_DASHBOARD_WIDGET_ORDER: DashboardConfigWidget[] = [
    { widget_code: "skip_meal_break", visible: true, sort_order: 9 },
    { widget_code: "who_is_in", visible: true, sort_order: 10 },
    { widget_code: "my_timesheet", visible: true, sort_order: 20 },
    { widget_code: "team_timesheet", visible: true, sort_order: 30 },
    { widget_code: "module_timeoff", visible: true, sort_order: 40 },
    { widget_code: "my_schedule", visible: true, sort_order: 50 },
    { widget_code: "shift_planner", visible: true, sort_order: 55 },
    { widget_code: "remote_work_counter", visible: true, sort_order: 65 },
    { widget_code: "paris_work_counter", visible: true, sort_order: 66 },
    { widget_code: "webclock", visible: true, sort_order: 70 },
    { widget_code: ACTION_REQUIRED_DASHBOARD_WIDGET_CODE, visible: true, sort_order: 1000 },
];

export const DEFAULT_DASHBOARD_WIDGET_VISIBILITY = {
    ACTION_REQUIRED: true,
    WHO_IS_IN: true,
    MY_SCHEDULE: true,
    MODULE_TIMEOFF: true,
    TIMECARD: true,
    REMOTE_WORK_COUNTER: true,
    PARIS_WORK_COUNTER: true,
};

export const ensureActionRequiredDashboardWidget = (widgets: DashboardConfigWidget[]): DashboardConfigWidget[] => {
    const hasActionRequiredWidget = widgets.some((widget) =>
        ACTION_REQUIRED_DASHBOARD_WIDGET_CODES.includes(widget.widget_code || "")
    );

    if (hasActionRequiredWidget) {
        return widgets;
    }

    return [
        { widget_code: ACTION_REQUIRED_DASHBOARD_WIDGET_CODE, visible: true, sort_order: 1000 },
        ...widgets,
    ].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
};

const getDashboardWidgetUrl = (widget: DashboardConfigWidget): string => {
    const config = widget.config;

    if (!config || Array.isArray(config)) {
        return "";
    }

    return typeof config.url === "string" ? config.url : "";
};

export const isDashboardCustomLinkWidget = (widget: DashboardConfigWidget): boolean => {
    return Boolean(getDashboardWidgetUrl(widget));
};

export const getDashboardDisplayWidgets = (dashboardConfigWidgets: DashboardConfigWidget[]): DashboardConfigWidget[] => {
    return dashboardConfigWidgets.filter((widget) => !isDashboardCustomLinkWidget(widget));
};

export const getLegacyDashboardCustomLinks = (dashboardConfigWidgets: DashboardConfigWidget[]): DashboardCustomLink[] => {
    return dashboardConfigWidgets
        .filter((widget) => Boolean(widget.visible) && isDashboardCustomLinkWidget(widget))
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .map((widget) => ({
            code: widget.widget_code,
            title: widget.title || widget.widget_code || "Quick Link",
            url: getDashboardWidgetUrl(widget),
            icon: widget.icon,
            label: widget.title || widget.widget_code || "Quick Link",
            order: widget.sort_order,
            widgetCode: widget.widget_code,
            sort_order: widget.sort_order,
            target: "_self",
        }));
};

export const normalizeDashboardConfigCustomLinks = (customLinks: DashboardConfigUrl[] = []): DashboardConfigUrl[] => {
    return customLinks
        .filter((customLink) => Boolean(customLink?.url))
        .map((customLink, index) => ({
            code: customLink.code || `custom_link_${index + 1}`,
            title: customLink.title || "Quick Link",
            url: customLink.url,
            icon: customLink.icon,
            sort_order: customLink.sort_order,
            target: (customLink.target === "_blank" ? "_blank" : "_self") as "_self" | "_blank",
        }))
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
};

export const isDashboardWidgetEnabled = (
    dashboardConfigWidgets: DashboardConfigWidget[],
    useDashboardConfig: boolean,
    widgetCode?: string,
    fallbackVisibility?: boolean
): boolean => {
    if (!widgetCode) {
        return false;
    }

    if (useDashboardConfig) {
        return Boolean(dashboardConfigWidgets.find((widget) => widget.widget_code === widgetCode)?.visible);
    }

    return Boolean(fallbackVisibility);
};

export const isAnyDashboardWidgetEnabled = (
    dashboardConfigWidgets: DashboardConfigWidget[],
    useDashboardConfig: boolean,
    widgetCodes: string[],
    fallbackVisibility?: boolean
): boolean => {
    if (useDashboardConfig) {
        return widgetCodes.some((widgetCode) =>
            Boolean(dashboardConfigWidgets.find((widget) => widget.widget_code === widgetCode)?.visible)
        );
    }

    return Boolean(fallbackVisibility);
};

export const getDashboardDateRange = (): string[] => {
    const today = new Date();
    const start = subDays(today, 30);
    const end = addDays(today, 30);

    return eachDayOfInterval({ start, end }).map((date) => format(date, "yyyy-MM-dd"));
};
